import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { DocumentPage } from 'Components/DocumentPage';
import { PDFDocument } from 'Components/PDFDocument';
import { RequisiteSelectModal } from 'Components/RequisiteComponents';
import UIButton from 'Components/UIComponents/UIButton';
import UISpinner from 'Components/UIComponents/UISpinner';
import {
  useBooleanRefState,
  useDisposableCallRef,
  useModal,
  useRefState,
} from 'Hooks/Common';
import { useDocumentFieldUpdateLocally } from 'Hooks/DocumentFields';
import { useFieldShape, useInteractLeaveModal } from 'Hooks/Interact';
import interact from 'interactjs';
import { DocumentTypes, Signer } from 'Interfaces/Document';
import {
  DocumentField,
  DocumentFieldAction,
  DocumentFieldActions,
  DocumentFieldAddType,
  DocumentFieldsCRUDMeta,
  DocumentFieldTypes,
  DocumentFieldUpdatePayload,
  FieldCreateType,
} from 'Interfaces/DocumentFields';
import {
  defaultRequisiteTypesOrder,
  Requisite,
  RequisiteType,
} from 'Interfaces/Requisite';
import { User } from 'Interfaces/User';
import { flatten, isEmpty, minBy, throttle } from 'lodash-es';
import { fontFamilyByValue, interactInitialFont } from 'Services/Fonts';
import Interact from 'Services/Interact';
import Toast from 'Services/Toast';
import { rootActions } from 'Store';
import { checkIfDateOrText, getCurrentDate, isRequisite } from 'Utils/functions';
import {
  selectDocument,
  selectDocumentFields,
  selectDocumentPdfMetadataFromDocumentPart,
  selectDocumentSigner,
  selectDocumentSignerOptions,
  selectInteractLoadedPagesCount,
  selectInteractMobileCurrentPageWidth,
  selectInteractPagesPerDocument,
  selectInteractPdfDocumentFiles,
  selectInteractRenderedPages,
  selectInteractTotalPagesCount,
  selectIsAllInteractPagesRendered,
  selectIsAllInteractPdfFilesLoaded,
  selectLastInteractedField,
  selectUser,
} from 'Utils/selectors';
import uuid from 'uuid/v4';

import { INTERACT_SIZES } from '../../../common/constants';
import { defaultSizesByFieldType } from '../../../common/fieldShapes';
import { BaseInteractModalProps } from '../../../common/interfaces';
import { createPitchToZoom } from '../../../common/pitchToZoom';
import {
  calculateSizeRatio,
  changeFieldDropDownPositionInvalidity,
  updateScrollOnScale,
} from '../../../common/utils';
import ApplyFontsModal from '../../../components/ApplyFontsModal';
import InteractMobileFieldBar from '../../../components/InteractMobileFieldBar';
import InteractMobileNavigation from '../../../components/InteractMobileNavigation';
import InteractOpenSignPanelButton from '../../../components/InteractOpenSignPanelButton';
import MeOptionModal from '../../../components/MeOptionModal';
import ModalHeader from '../../../components/ModalHeader';
import { FieldItem } from '../../FieldItem';

export function BaseInteractModalMobile({
  documentId,
  onClose,
  handleSubmit,
  submitting,
  buttonSendTitle,
  createDocumentField,
  deleteDocumentField,
  updateDocumentField,
  updateDocument,
  cancelDocumentUpdate,
  isDisableCancelButton,
  isDisablePreparerSigner,
  dateFormat,
  availableSignatureTypes,
  isDocumentInteractLoading,
  redoDocumentFieldAction,
  undoDocumentFieldAction,
  isNextAvailable,
  isPrevAvailable,
  enableFullscreen = false,
}: BaseInteractModalProps) {
  const documentIdObject = useMemo(() => ({ documentId }), [documentId]);

  const currentDocument = useSelector(state => selectDocument(state, documentIdObject));

  const pdfPageMeta = useSelector(state =>
    selectDocumentPdfMetadataFromDocumentPart(state, documentIdObject),
  );

  const pdfDocumentFiles = useSelector(state =>
    selectInteractPdfDocumentFiles(state, documentIdObject),
  );

  const pagesPerDocument = useSelector(state =>
    selectInteractPagesPerDocument(state, documentIdObject),
  );

  const totalPagesCount = useSelector(state =>
    selectInteractTotalPagesCount(state, documentIdObject),
  );

  const isAllPdfFilesLoaded = useSelector(state =>
    selectIsAllInteractPdfFilesLoaded(state, documentIdObject),
  );

  const loadedPagesCount = useSelector(state =>
    selectInteractLoadedPagesCount(state, documentIdObject),
  );

  const renderedPages = useSelector(state =>
    selectInteractRenderedPages(state, documentIdObject),
  );

  const isAllPagesRendered = useSelector(state =>
    selectIsAllInteractPagesRendered(state, documentIdObject),
  );

  const dispatch = useDispatch();

  const documentSignersSelectorProps = useMemo(
    () => ({
      documentId,
      isPreparerSigner: !isDisablePreparerSigner,
    }),
    [documentId, isDisablePreparerSigner],
  );

  const signersOptions = useSelector(state =>
    selectDocumentSignerOptions(state, documentSignersSelectorProps),
  );

  const documentFields: DocumentField[] = useSelector(selectDocumentFields);

  const [afterModalOpenRef, afterModalCallAndDispose] = useDisposableCallRef();
  const containerRef = useRef<HTMLDivElement>(null);
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const [
    documentViewContainer,
    setDocumentViewContainer,
  ] = useState<HTMLDivElement | null>(null);
  const clipboardRef = useRef<DocumentField | null>(null);
  const [enableResizeFont, setEnableResizeFont] = useState<boolean>(true);
  const [mobileCurrentPage, setMobileCurrentPage] = useState<number>(1);

  const [baseScale, setBaseScale] = useState<number>(1);
  const [selectedScale, setSelectedScale] = useState<number>(1);
  const documentScale = baseScale * selectedScale;

  const [selectedFieldType, setSelectedFieldType] = useState<
    DocumentFieldTypes | undefined
  >();
  const [lastInteractedFieldId, setLastInteractedFieldId] = useState<string | null>(null);
  const [currentFieldAction, setCurrentFieldAction] = useState<
    DocumentFieldAction<DocumentFieldActions> | undefined
  >();
  const [meOptionSelected, setMeOptionSelected] = useState(false);
  const [isFieldShapeExist, setFieldShapeExist] = useState<boolean>(false);

  const [generalPageArray, setGeneralPageArray] = useState<ChildNode[]>([]);

  const isPageRendered = useCallback(
    (absolutePageNumber: number) => renderedPages[absolutePageNumber],
    [renderedPages],
  );

  const greaterPageWidth = useSelector(state =>
    selectInteractMobileCurrentPageWidth(state, {
      documentId,
      currentPage: mobileCurrentPage,
    }),
  );

  const [openMeOptionModal, closeMeOptionModal] = useModal(() => (
    <MeOptionModal
      onClose={() => {
        closeMeOptionModal();
        afterModalCallAndDispose();
      }}
    />
  ));

  const [mainFieldProps, setMainFieldProps] = useState<{
    fontSize?: number;
    fontFamily?: string;
  }>(interactInitialFont);
  const updateDocumentFieldLocally = useDocumentFieldUpdateLocally();

  const {
    ref: isAbleToDeleteLastInteractedFieldRef,
    enableValue: enableToDeleteLastInteractedField,
    disableValue: disableToDeleteLastInteractedField,
  } = useBooleanRefState(true);

  const {
    ref: focusedFieldIdRef,
    setValue: setFocusedFieldId,
    clearValue: unsetFocusedFieldId,
  } = useRefState<string>();

  const {
    fieldRootRef,
    fieldShapeContainerRef,
    removeFieldShape,
    addInvalidityToFieldShape,
    removeInvalidityFromFieldShape,
  } = useFieldShape({
    isFieldShapeExist,
    onRemoveFieldShape: () => {
      setFieldShapeExist(false);
      setSelectedFieldType(undefined);
    },
  });

  const deleteFieldShape = useCallback(() => {
    setFieldShapeExist(false);
    setSelectedFieldType(undefined);
    removeFieldShape();
  }, [removeFieldShape, setFieldShapeExist, setSelectedFieldType]);

  const lastInteractedField = useSelector(state =>
    selectLastInteractedField(state, {
      ...documentSignersSelectorProps,
      lastInteractedFieldId,
    }),
  );
  const lastInteractedFieldSigner = useSelector(state =>
    selectDocumentSigner(state, { documentId, signerId: lastInteractedField?.signerId }),
  );

  const { name: userName } = useSelector(selectUser) as User;

  useEffect(() => {
    return () => {
      cancelDocumentUpdate();
    };
  }, [documentId, cancelDocumentUpdate, currentDocument]);

  useEffect(() => {
    dispatch(rootActions.documentInteract.resetPagesRendered(documentId));
  }, [dispatch, documentId]);

  const [, { resetIfBlocked }] = useInteractLeaveModal();

  const isPreparingInteract = loadedPagesCount !== totalPagesCount;
  const lowScale = selectedScale < 1;

  const getGeneralArrayOfPagesComponents = useCallback(() => {
    const fileNodes = pagesContainerRef.current?.childNodes;

    if (fileNodes) {
      const pageArray: ChildNode[] = [];

      fileNodes.forEach(child => {
        pageArray.push(...flatten(child.childNodes));
      });

      setGeneralPageArray(pageArray);

      return pageArray;
    }
  }, []);

  const getFieldPageComponent = useCallback(
    (pageNumber: DocumentField['pageNumber']) => {
      const pages = getGeneralArrayOfPagesComponents() as HTMLElement[];

      return pages[pageNumber - 1];
    },
    [getGeneralArrayOfPagesComponents],
  );

  const newFieldSigner = useMemo(
    () =>
      currentDocument?.signers.reduce((accumSigner, signer) => {
        if (lastInteractedFieldSigner && !lastInteractedFieldSigner.isPreparer) {
          return lastInteractedFieldSigner;
        }

        if (!accumSigner || accumSigner.isPreparer) return signer;

        return accumSigner;
      }, undefined as Signer | undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentDocument?.signers, lastInteractedFieldSigner],
  );

  const [openRequisiteSelectModal, closeRequisiteSelectModal, isModalOpen] = useModal(
    () => (
      <RequisiteSelectModal
        onClose={() => {
          setCurrentFieldAction(undefined);
          closeRequisiteSelectModal();
          afterModalCallAndDispose();
        }}
        type={
          currentFieldAction?.payload?.type === DocumentFieldTypes.Signature
            ? RequisiteType.SIGN
            : RequisiteType.INITIAL
        }
        availableSignatureTypes={
          currentFieldAction?.payload?.availableSignatureTypes ||
          defaultRequisiteTypesOrder
        }
        onSelect={(requisite: Requisite) => {
          if (!currentFieldAction) return;

          setCurrentFieldAction({
            ...currentFieldAction,
            payload: {
              ...currentFieldAction.payload,
              requisiteId: requisite.id,
            },
          });
          closeRequisiteSelectModal();
        }}
      />
    ),
    [currentFieldAction],
  );

  const setFieldCreateAction = useCallback(
    (fieldData: DocumentFieldAddType, meta?: DocumentFieldsCRUDMeta) => {
      const { type } = fieldData;
      const isDateOrText = checkIfDateOrText(type);
      const defaultSizes = defaultSizesByFieldType[type];
      const fieldPageComponent = getFieldPageComponent(fieldData.pageNumber);

      const fontStyles = isDateOrText
        ? {
            fontSize: interactInitialFont.fontSize,
            fontFamily: interactInitialFont.fontFamily,
          }
        : undefined;
      const coordinateX =
        fieldData.coordinateX + defaultSizes.width > fieldPageComponent.clientWidth
          ? fieldData.coordinateX -
            (fieldData.coordinateX + defaultSizes.width - fieldPageComponent.clientWidth)
          : fieldData.coordinateX;
      const coordinateY =
        fieldData.coordinateY + defaultSizes.height > fieldPageComponent.clientHeight
          ? fieldData.coordinateY -
            (fieldData.coordinateY +
              defaultSizes.height -
              fieldPageComponent.clientHeight)
          : fieldData.coordinateY;
      const newField: DocumentField = {
        id: uuid(),
        documentId,
        dateFormat: fieldData.type === DocumentFieldTypes.Date ? dateFormat : null,
        requisiteId: null,
        availableSignatureTypes: isRequisite(fieldData.type)
          ? availableSignatureTypes
          : null,
        checked:
          fieldData.type === DocumentFieldTypes.Checkbox
            ? newFieldSigner?.isPreparer
            : null,
        ...fontStyles,
        ...defaultSizes,
        ...fieldData,
        text: newFieldSigner?.isPreparer
          ? fieldData.type === DocumentFieldTypes.Date
            ? getCurrentDate(dateFormat)
            : fieldData.type === DocumentFieldTypes.Name
            ? userName
            : undefined
          : undefined,
        signerId: newFieldSigner?.id || null,
        coordinateX,
        coordinateY,
        required: true,
        style: {
          maxWidth: fieldPageComponent.clientWidth,
          maxHeight: fieldPageComponent.clientHeight,
          width: defaultSizes.width,
          height: defaultSizes.height,
          fontSize: fontStyles?.fontSize,
          fontFamily: fontFamilyByValue[fontStyles?.fontFamily as string],
          ...fieldData.style,
          left: coordinateX + fieldPageComponent?.offsetLeft,
          top: coordinateY + fieldPageComponent?.offsetTop,
        },
      };

      setCurrentFieldAction({
        type: DocumentFieldActions.CREATE,
        payload: newField,
        meta,
      });
    },
    [
      getFieldPageComponent,
      documentId,
      dateFormat,
      availableSignatureTypes,
      newFieldSigner?.isPreparer,
      newFieldSigner?.id,
      userName,
    ],
  );

  const setFieldUpdateAction = useCallback(
    (payload: DocumentFieldUpdatePayload, meta?: DocumentFieldsCRUDMeta) => {
      const sizePayload = {};

      if (payload.width && payload.coordinateX && payload.pageNumber) {
        const fieldPageComponent = getFieldPageComponent(payload.pageNumber);
        const newWidth =
          payload.coordinateX + payload.width > fieldPageComponent.clientWidth
            ? fieldPageComponent.clientWidth - payload.coordinateX
            : payload.width;

        Object.assign(sizePayload, {
          width: newWidth,
          style: {
            ...payload.style,
            width: newWidth,
          },
        });
      }

      if (meta?.stopFontAutoSize) {
        setEnableResizeFont(false);
      }

      setCurrentFieldAction({
        type: DocumentFieldActions.UPDATE,
        payload: { ...payload, ...sizePayload },
        meta,
      });
    },
    [getFieldPageComponent],
  );

  const addField = useCallback(
    (action: DocumentFieldAction<DocumentFieldActions.CREATE>) => {
      createDocumentField(action.payload, action.meta);
      setLastInteractedFieldId(action.payload.id);
    },
    [createDocumentField],
  );

  const updateField = useCallback(
    (action: DocumentFieldAction<DocumentFieldActions.UPDATE>) => {
      updateDocumentField(action.payload, action.meta);
    },
    [updateDocumentField],
  );

  const executeCurrentFieldAction = useCallback(
    (action: DocumentFieldAction<DocumentFieldActions>) => {
      switch (action.type) {
        case DocumentFieldActions.CREATE: {
          return addField(action as DocumentFieldAction<DocumentFieldActions.CREATE>);
        }
        case DocumentFieldActions.UPDATE: {
          return updateField(action as DocumentFieldAction<DocumentFieldActions.UPDATE>);
        }
        default:
          return;
      }
    },
    [addField, updateField],
  );

  useEffect(() => {
    if (!currentFieldAction || !currentDocument) return () => {};

    const fieldSigner = currentDocument?.signers.find(
      signer => signer.id === currentFieldAction.payload.signerId,
    );

    if (
      currentFieldAction.payload.type &&
      isRequisite(currentFieldAction.payload.type) &&
      fieldSigner?.isPreparer &&
      !currentFieldAction.payload.requisiteId
    ) {
      deleteFieldShape();
      return openRequisiteSelectModal();
    }

    setCurrentFieldAction(undefined);

    return executeCurrentFieldAction(currentFieldAction);
  }, [
    currentFieldAction,
    openRequisiteSelectModal,
    currentDocument,
    executeCurrentFieldAction,
    deleteFieldShape,
    setFieldShapeExist,
    setSelectedFieldType,
  ]);

  const recalculateDocumentFieldStyles = useCallback(
    (documentField: DocumentField) => {
      const fieldPageComponent = getFieldPageComponent(documentField.pageNumber);
      if (!fieldPageComponent) return;

      const updatedField = {
        ...documentField,
        style: {
          ...documentField.style,
          left: documentField.coordinateX + fieldPageComponent.offsetLeft,
          top: documentField.coordinateY + fieldPageComponent.offsetTop,
          fontFamily: documentField.fontFamily
            ? fontFamilyByValue[documentField.fontFamily]
            : undefined,
          fontSize: documentField.fontSize || undefined,
        },
      };

      updateDocumentFieldLocally(updatedField);
    },
    [getFieldPageComponent, updateDocumentFieldLocally],
  );

  useEffect(() => {
    if (pagesContainerRef.current && isAllPdfFilesLoaded && isAllPagesRendered) {
      documentFields.forEach(recalculateDocumentFieldStyles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesContainerRef.current, isAllPdfFilesLoaded, isAllPagesRendered]);

  const handleDocumentScaleChange = useCallback(
    (nextSelectedScale: number, anchor?: { clientX: number; clientY: number }) => {
      const currentDocumentScale = documentScale;
      const nextDocumentScale = baseScale * nextSelectedScale;

      updateScrollOnScale({
        element: documentViewContainer,
        anchor,
        documentScale: currentDocumentScale,
        nextDocumentScale,
      });

      documentFields.forEach(recalculateDocumentFieldStyles);

      setSelectedScale(nextSelectedScale);
    },
    [
      documentViewContainer,
      documentFields,
      baseScale,
      documentScale,
      recalculateDocumentFieldStyles,
    ],
  );

  useEffect(() => {
    if (!documentViewContainer) return;

    const pitchToZoom = createPitchToZoom(documentViewContainer, {
      scale: selectedScale,
      onTouchMoveComplete: handleDocumentScaleChange,
    });
    pitchToZoom.attachListeners();
    return () => void pitchToZoom.detachListeners();
  }, [documentViewContainer, selectedScale, handleDocumentScaleChange]);

  const updateContainerBaseRatio = useCallback(
    (container: HTMLElement | null) => {
      const baseRatio = calculateSizeRatio(greaterPageWidth, container);
      if (typeof baseRatio !== 'undefined' && baseRatio > 0) {
        setBaseScale(baseRatio);
      }
    },
    [greaterPageWidth],
  );

  useEffect(() => {
    if (!documentViewContainer || isPreparingInteract) return;

    function resize() {
      updateContainerBaseRatio(documentViewContainer);
    }

    resize();
    const throttledResize = throttle(resize, 750);

    window.addEventListener('resize', throttledResize);
    return () => window.removeEventListener('resize', throttledResize);
  }, [documentViewContainer, isPreparingInteract, updateContainerBaseRatio]);

  const handleFieldDropOnPageContainer = useCallback(
    event => {
      changeFieldDropDownPositionInvalidity(event, false);
      const targetFieldNode = event.dragEvent.target as HTMLDivElement;
      const isClone = targetFieldNode.getAttribute('data-clone') === 'true';
      if (isClone) {
        return targetFieldNode.remove();
      }
      const targetFieldId = targetFieldNode.id;
      const targetDocumentField = documentFields.find(
        documentField => documentField.id === targetFieldId,
      ) as DocumentField;
      const targetFieldStyles = targetDocumentField?.style;
      if (targetFieldStyles?.left) {
        targetFieldNode.style.left = `${targetFieldStyles.left}px`;
        targetFieldNode.setAttribute('data-coord-x', targetFieldStyles.left.toString());
      }
      if (targetFieldStyles?.top) {
        targetFieldNode.style.top = `${targetFieldStyles.top}px`;
        targetFieldNode.setAttribute('data-coord-y', targetFieldStyles.top.toString());
      }
    },
    [documentFields],
  );

  const handleFieldDropOnPage = useCallback(
    (event, pageNumber: number) => {
      const { dragEvent, target: targetPage } = event;
      const targetFieldNode = dragEvent.target as HTMLElement;
      const fieldRect = interact.getElementRect(targetFieldNode);
      const dropzoneRect = interact.getElementRect(targetPage);
      const isClone = targetFieldNode.getAttribute('data-clone') === 'true';
      const offsetX = Math.abs(fieldRect.left - dropzoneRect.left) / documentScale;
      const offsetY = Math.abs(fieldRect.top - dropzoneRect.top) / documentScale;
      const positioning = {
        coordinateX: offsetX,
        coordinateY: offsetY,
        style: {
          left: offsetX + targetPage.offsetLeft,
          top: offsetY + targetPage.offsetTop,
        },
      };

      if (isClone && documentViewContainer) {
        const type = targetFieldNode.getAttribute(
          'data-field-type',
        ) as DocumentFieldTypes;

        setFieldCreateAction({
          signerId: null,
          required: false,
          type,
          pageNumber: pageNumber,
          ...positioning,
          createType: FieldCreateType.ADD,
        });

        return targetFieldNode.remove();
      }

      const targetFieldId = targetFieldNode.id;
      const targetField = documentFields.find(
        documentField => documentField.id === targetFieldId,
      ) as DocumentField;

      setFieldUpdateAction({
        ...targetField,
        pageNumber: pageNumber,
        ...positioning,
        style: {
          ...targetField.style,
          ...positioning.style,
        },
      });
    },
    [
      setFieldCreateAction,
      setFieldUpdateAction,
      documentFields,
      documentViewContainer,
      documentScale,
    ],
  );

  useEffect(() => {
    const draggableFieldItem = interact(`div[data-clone=true]`)
      .draggable({
        onmove: Interact.dragMoveListener,
      })
      .on('down', event => {
        const { interaction } = event;

        interaction.start({ name: 'drag' }, event.interactable, event.currentTarget);
      });

    return () => draggableFieldItem.unset();
  }, []);

  useEffect(() => {
    if (isAllPdfFilesLoaded && pagesContainerRef.current) {
      getGeneralArrayOfPagesComponents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllPdfFilesLoaded, pagesContainerRef.current, getGeneralArrayOfPagesComponents]);

  useEffect(() => {
    if (containerRef.current) {
      const listeners = interact(containerRef.current).dropzone({
        overlap: 0,
        ondragenter: event => changeFieldDropDownPositionInvalidity(event, true),
        ondragleave: event => changeFieldDropDownPositionInvalidity(event, false),
        ondrop: handleFieldDropOnPageContainer,
      });

      return () => listeners.unset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFieldDropOnPageContainer, documentFields, containerRef.current]);

  const handleSignerChange = (signerId: string) => {
    signersOptions.forEach(signer => {
      if (signer.value === signerId && signer.label === 'Me (now)') {
        if (!meOptionSelected) {
          openMeOptionModal();
          setMeOptionSelected(true);
        }
      }
    });
  };

  const handleDocumentFieldsSubmit = useCallback(async () => {
    try {
      if (currentDocument) {
        const updateValues = {
          documentId: documentId,
          fields: documentFields,
          type: currentDocument.type,
        };

        await updateDocument({ values: updateValues, query: { log: true } });
        resetIfBlocked();
        await handleSubmit();

        Toast.success('Document successfully saved!');
      }
    } catch (error) {
      Toast.handleErrors(error);
    }
  }, [
    currentDocument,
    documentId,
    documentFields,
    updateDocument,
    resetIfBlocked,
    handleSubmit,
  ]);

  const handlePageTap = useCallback(
    (event, pageNumber: number) => {
      if (!isFieldShapeExist) {
        return;
      }
      setLastInteractedFieldId(null);

      if (selectedFieldType) {
        setFieldCreateAction({
          signerId: null,
          type: selectedFieldType,
          required: false,
          coordinateX: event.offsetX,
          coordinateY: event.offsetY,
          pageNumber: pageNumber,
          style: {
            left: event.offsetX + event.currentTarget.offsetLeft,
            top: event.offsetY + event.currentTarget.offsetTop,
          },
          createType: FieldCreateType.ADD,
        });

        deleteFieldShape();
      }
    },
    [isFieldShapeExist, selectedFieldType, deleteFieldShape, setFieldCreateAction],
  );

  const handlePageRendered = useCallback(
    (absolutePageNumber: number) => {
      dispatch(
        rootActions.documentInteract.setPageRendered({
          documentId,
          pageNumber: absolutePageNumber,
        }),
      );
    },
    [dispatch, documentId],
  );

  const copyDocumentField = useCallback(() => {
    clipboardRef.current =
      documentFields.find(documentField => documentField.id === lastInteractedFieldId) ||
      null;
  }, [documentFields, lastInteractedFieldId]);

  const deleteLastInteractedField = useCallback(() => {
    if (
      lastInteractedFieldSigner?.isPreparer &&
      focusedFieldIdRef.current === lastInteractedFieldId &&
      lastInteractedField?.type &&
      checkIfDateOrText(lastInteractedField?.type)
    ) {
      return;
    }

    if (lastInteractedFieldId) {
      deleteDocumentField({ id: lastInteractedFieldId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deleteDocumentField,
    lastInteractedField,
    lastInteractedFieldId,
    lastInteractedFieldSigner,
  ]);

  const getPagesRects = useCallback(() => {
    if (!pagesContainerRef.current) return;

    return Array.from({ length: generalPageArray.length }, (_, index) =>
      interact.getElementRect(generalPageArray[index] as HTMLElement),
    );
  }, [generalPageArray]);

  const getDocumentViewCenter = useCallback(() => {
    if (!documentViewContainer) return;

    const documentViewRect = interact.getElementRect(documentViewContainer);
    return documentViewRect.top + documentViewRect.height / 2;
  }, [documentViewContainer]);

  const findPageClosestToCenter = useCallback(() => {
    const pagesRects = getPagesRects();
    if (!pagesRects || !pagesRects.length) return;

    const pagesCenters = pagesRects
      .map((pageRect, index) => ({
        index,
        center: pageRect !== undefined ? pageRect.top + pageRect.height / 2 : 0,
      }))
      .filter(page => page.center > 0);

    const documentViewCenter = getDocumentViewCenter() as number;

    return minBy(pagesCenters, page =>
      Math.abs(documentViewCenter - page.center),
    ) as typeof pagesCenters[0];
  }, [getPagesRects, getDocumentViewCenter]);

  useEffect(() => {
    const closest = findPageClosestToCenter();
    if (closest) setMobileCurrentPage(closest.index + 1);
  }, [findPageClosestToCenter]);

  const pasteDocumentField = useCallback(() => {
    if (clipboardRef.current && pagesContainerRef.current && documentViewContainer) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...fieldData } = clipboardRef.current;

      if (focusedFieldIdRef.current) return;

      const closestPage = findPageClosestToCenter();

      if (!closestPage) return;

      const pageComponent = getFieldPageComponent(closestPage.index + 1);

      const pageCenterX = pageComponent?.clientWidth
        ? pageComponent.clientWidth / 2 - (fieldData.width as number) / 2
        : 0;
      const pageCenterY = pageComponent?.clientHeight
        ? pageComponent.clientHeight / 2 - (fieldData.height as number) / 2
        : 0;

      setFieldCreateAction({
        ...fieldData,
        pageNumber: (closestPage?.index || 0) + 1,
        coordinateX: pageCenterX,
        coordinateY: pageCenterY,
        style: {
          ...fieldData.style,
          left: pageComponent?.offsetLeft + pageCenterX,
          top: pageComponent?.offsetTop + pageCenterY,
        },
        createType: FieldCreateType.COPY,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findPageClosestToCenter, getFieldPageComponent, setFieldCreateAction]);

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (isModalOpen) {
        return;
      }

      if (
        isAbleToDeleteLastInteractedFieldRef.current &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        return deleteLastInteractedField();
      }

      if (event.metaKey || event.ctrlKey) {
        switch (event.key.toLocaleLowerCase()) {
          case 'c': {
            return copyDocumentField();
          }
          case 'v': {
            return pasteDocumentField();
          }
          case 'z': {
            return event.shiftKey ? redoDocumentFieldAction() : undoDocumentFieldAction();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isModalOpen,
      copyDocumentField,
      deleteLastInteractedField,
      pasteDocumentField,
      redoDocumentFieldAction,
      undoDocumentFieldAction,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);

    return () => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const validateTag = useCallback(
    (id: string, tag: string) =>
      tag ? documentFields.every(field => field.tag !== tag || field.id === id) : true,
    [documentFields],
  );

  const updateForEachFontProps = () => {
    documentFields.forEach(field => {
      updateDocumentField(
        {
          ...field,
          fixedFontSize: true,
          fontFamily: mainFieldProps?.fontFamily,
          fontSize: mainFieldProps?.fontSize,
          style: {
            fontFamily:
              fontFamilyByValue[
                mainFieldProps.fontFamily || interactInitialFont.fontFamily
              ],
            fontSize: mainFieldProps?.fontSize,
          },
        },
        {
          pushToHistory: true,
        },
      );
    });
  };

  const [openApplyFontsModal, closeApplyFontsModal] = useModal(() => {
    const onClose = () => {
      closeApplyFontsModal();
      afterModalCallAndDispose();
    };
    const onContinue = () => {
      updateForEachFontProps();
      onClose();
    };
    return (
      <ApplyFontsModal
        documentType={currentDocument?.type}
        onClose={onClose}
        onContinue={onContinue}
      />
    );
  }, [currentDocument?.type, updateForEachFontProps]);

  const handleApplyFontToEachField = (fontFamily: string, fontSize: number) => {
    setMainFieldProps({
      fontSize,
      fontFamily,
    });
    openApplyFontsModal();
  };

  const recalculateFieldStyle = useCallback(
    (field: DocumentField): DocumentField => {
      if (!pagesContainerRef.current) return field;

      const fieldPageComponent = getFieldPageComponent(field.pageNumber);

      return {
        ...field,
        style: {
          ...field.style,
          left: field.coordinateX + fieldPageComponent?.offsetLeft,
          top: field.coordinateY + fieldPageComponent?.offsetTop,
        },
      };
    },
    [getFieldPageComponent],
  );

  const handlePdfDocumentLoadSuccess = useCallback(
    (pdfDocumentFile: string, pdfDocumentProxy) => {
      dispatch(
        rootActions.documentInteract.setPagesForFile({
          documentId,
          pdfFile: pdfDocumentFile,
          pagesNumber: pdfDocumentProxy.numPages,
        }),
      );
    },
    [dispatch, documentId],
  );

  const getAbsolutePageNumber = useCallback(
    (pdfDocumentFileIdx: number, pageNumber: number) =>
      pdfDocumentFiles
        .slice(0, pdfDocumentFileIdx)
        .map(key => pagesPerDocument[key].length)
        .reduce((a, b) => a + b, pageNumber),
    [pagesPerDocument, pdfDocumentFiles],
  );

  const [openMobileFieldBar, closeMobileFieldBar] = useModal(
    () => (
      <InteractMobileFieldBar
        wizardFormStep={2}
        selectedScale={selectedScale}
        selectedFieldType={selectedFieldType}
        setSelectedFieldType={setSelectedFieldType}
        setCurrentDocumentType={() => {}}
        setFieldShapeExist={setFieldShapeExist}
        fieldRootRef={fieldRootRef}
        containerRef={containerRef}
        fieldShapeContainerRef={fieldShapeContainerRef}
        documentScale={documentScale}
        isDocumentFirstAndDisabled={false}
        field={lastInteractedField}
        lastInteractedFieldId={lastInteractedFieldId}
        isInteractPreparing={isPreparingInteract}
        isLoading={submitting}
        isChooseActionDisabled={false}
        participantOptions={signersOptions}
        onClose={closeMobileFieldBar}
        onSubmit={handleDocumentFieldsSubmit}
        onFieldUpdate={setFieldUpdateAction}
        onFieldDelete={deleteDocumentField}
        onFieldShapeRemove={removeFieldShape}
        onSignerChange={handleSignerChange}
        onStartInputTyping={disableToDeleteLastInteractedField}
        onStopInputTyping={enableToDeleteLastInteractedField}
        onApplyFontToEachField={(fontFamily: string, fontSize: number) => {
          closeMobileFieldBar();
          handleApplyFontToEachField(fontFamily, fontSize);
          afterModalOpenRef.current = openMobileFieldBar;
        }}
        validateTag={validateTag}
      />
    ),
    [
      selectedFieldType,
      setSelectedFieldType,
      setFieldShapeExist,
      containerRef,
      fieldShapeContainerRef,
      documentScale,
      lastInteractedField,
      lastInteractedFieldId,
      isPreparingInteract,
      submitting,
      signersOptions,
      handleDocumentFieldsSubmit,
      setFieldUpdateAction,
      deleteDocumentField,
      removeFieldShape,
      handleSignerChange,
      disableToDeleteLastInteractedField,
      enableToDeleteLastInteractedField,
      handleApplyFontToEachField,
      validateTag,
    ],
  );

  return (
    <ReactModal
      className={classNames('interactModal', { fullscreen: enableFullscreen })}
      overlayClassName="modal"
      isOpen
    >
      <div
        className="fieldShape invalid"
        ref={fieldShapeContainerRef}
        style={{
          position: 'absolute',
          zIndex: 1000,
          width: 'max-content',
          pointerEvents: 'none',
        }}
      />
      {isDocumentInteractLoading ? (
        <UISpinner width={50} height={50} wrapperClassName="spinner--main__wrapper" />
      ) : (
        <div
          ref={containerRef}
          className={classNames('interactModal__inner', { fullscreen: enableFullscreen })}
        >
          <ModalHeader
            handleClose={onClose}
            onScaleChange={handleDocumentScaleChange}
            documentScale={selectedScale}
            handleSubmit={handleDocumentFieldsSubmit}
            isSubmitting={submitting}
            onCopy={copyDocumentField}
            onPaste={pasteDocumentField}
            submitButtonTitle={buttonSendTitle}
            isForm={currentDocument?.type === DocumentTypes.FORM_REQUEST}
            isDisableCancelButton={isDisableCancelButton}
            redoDocumentFieldAction={redoDocumentFieldAction}
            undoDocumentFieldAction={undoDocumentFieldAction}
            isNextAvailable={isNextAvailable}
            isPrevAvailable={isPrevAvailable}
            showNavigateButtons={false}
            showFormButtons={false}
          />
          <div
            className={classNames('interactModal__body', 'template', {
              fullscreen: enableFullscreen,
            })}
          >
            <div className="interactModal__documentView" ref={setDocumentViewContainer}>
              <div style={{ height: 0 }}>
                <div
                  className="interactModal__documentView-inner"
                  style={{
                    transformOrigin: `${lowScale ? 'center' : 'left'} top`,
                    width: greaterPageWidth,
                    position: lowScale ? 'relative' : 'static',
                    left: lowScale ? '50%' : undefined,
                    transform: lowScale
                      ? `translateX(-50%) scale(${documentScale})`
                      : `scale(${documentScale})`,
                  }}
                >
                  <div
                    onMouseEnter={removeInvalidityFromFieldShape}
                    onMouseLeave={addInvalidityToFieldShape}
                    ref={pagesContainerRef}
                  >
                    {pdfDocumentFiles.map((pdfDocumentFile, pdfDocumentFileIdx) => (
                      <PDFDocument
                        key={pdfDocumentFile}
                        file={pdfDocumentFile}
                        loading={<></>}
                        onLoadSuccess={proxy =>
                          handlePdfDocumentLoadSuccess(pdfDocumentFile, proxy)
                        }
                      >
                        {isAllPdfFilesLoaded &&
                          pagesPerDocument[pdfDocumentFile].map(pageNumber => (
                            <DocumentPage
                              key={pageNumber}
                              pageNumber={pageNumber}
                              className={classNames({
                                'interactModal--cursor-pointer': selectedFieldType,
                              })}
                              renderAnnotationLayer={false}
                              style={{
                                height:
                                  pdfPageMeta[
                                    getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber)
                                  ].height,
                                width:
                                  pdfPageMeta[
                                    getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber)
                                  ].width,
                                marginBottom: INTERACT_SIZES.pageMarginBottom,
                                display:
                                  isAllPdfFilesLoaded &&
                                  getAbsolutePageNumber(
                                    pdfDocumentFileIdx,
                                    pageNumber,
                                  ) !== mobileCurrentPage
                                    ? 'none'
                                    : undefined,
                              }}
                              onLoad={() =>
                                handlePageRendered(
                                  getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber),
                                )
                              }
                              onDrop={event =>
                                handleFieldDropOnPage(
                                  event,
                                  getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber),
                                )
                              }
                              onTap={event =>
                                handlePageTap(
                                  event,
                                  getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber),
                                )
                              }
                            />
                          ))}
                      </PDFDocument>
                    ))}
                  </div>
                  {documentFields
                    .filter(field => !isEmpty(field.style))
                    .filter(field => isPageRendered(field.pageNumber))
                    .filter(field => field.pageNumber === mobileCurrentPage)
                    .map(field => recalculateFieldStyle(field))
                    .map(field => (
                      <FieldItem
                        key={field.id}
                        field={field}
                        documentScale={documentScale}
                        signersOptions={signersOptions}
                        onChangeSigner={handleSignerChange}
                        disabled={isPreparingInteract}
                        onInteract={setLastInteractedFieldId}
                        onFocus={setFocusedFieldId}
                        onBlur={unsetFocusedFieldId}
                        onDocumentFieldUpdate={setFieldUpdateAction}
                        isFontResize={enableResizeFont}
                        selectedScale={selectedScale}
                        popupAvailable={false}
                      />
                    ))}
                </div>
              </div>
            </div>
            <ul className="interactModal__fieldBar-bottomBar-list">
              <li className="interactModal__fieldBar-bottomBar-item-wrapper interactModal__fieldBar-bottomBar-item--openSignPanelButton">
                <InteractOpenSignPanelButton
                  className="interactModal__fieldBar-selectField-item-openSignPanel-button--float"
                  onClick={openMobileFieldBar}
                />
              </li>
              <li className="interactModal__fieldBar-bottomBar-item-wrapper interactModal__fieldBar-bottomBar-item--navigation">
                <InteractMobileNavigation
                  documents={pdfDocumentFiles}
                  pages={pagesPerDocument}
                  isLoaded={isAllPdfFilesLoaded}
                  currentAbsolutePage={mobileCurrentPage}
                  onNavigate={(documentFileIdx, pageNumber) =>
                    setMobileCurrentPage(
                      getAbsolutePageNumber(documentFileIdx, pageNumber),
                    )
                  }
                />
              </li>
              <li className="interactModal__fieldBar-bottomBar-item-wrapper interactModal__fieldBar-bottomBar-item--send">
                <div className="interactModal__header-send">
                  <UIButton
                    className="interactModal__header-send-button"
                    priority="white"
                    title="Cancel"
                    handleClick={onClose}
                  />
                  <UIButton
                    className="interactModal__header-send-button"
                    priority="primary"
                    title={buttonSendTitle}
                    disabled={isDocumentInteractLoading || submitting}
                    isLoading={submitting}
                    handleClick={handleDocumentFieldsSubmit}
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}
    </ReactModal>
  );
}

export default BaseInteractModalMobile;
